import { useMemo, useState } from 'react';

import { Modal, ModalFooter, Typography } from '@kc-console/common-ui';
import {
  useAllRegionMegaMenuQuery,
  useEditFavoriteService,
  useToast,
  ViewPoint,
} from '@kc-console/context';
import { useTranslation } from '@kc-console/core';

import { MAX_FAVORITE_SERVICE_COUNT } from '../../hooks';
import FavoriteModalBody from './FavoriteModalBody';

import type { ModalComponentProps } from '@kc-console/context';

export type AddFavoriteModalProps = ModalComponentProps<{ initialSelected?: string[] }>;

const AddFavoriteModal = (props: AddFavoriteModalProps) => {
  const { initialSelected = [], onClose, onSubmit, open = false, TransitionProps } = props;
  const { t } = useTranslation();
  const { enqueueErrorToast, enqueueSuccessToast } = useToast();
  const [selected, setSelected] = useState<string[]>(initialSelected);

  const { mutate: editFavoriteService, isPending } = useEditFavoriteService();

  const { data: allRegionCategories = [], isLoading } = useAllRegionMegaMenuQuery();

  /**
   * 즐겨찾기 선택지로 노출할 서비스 목록.
   * @description 현재 리전과 무관하게 region='all' 통합 메가메뉴에서 ViewPoint.MEGAMENU 서비스만 남깁니다.
   */
  const allServices = useMemo(
    () =>
      allRegionCategories
        .map(category => ({
          ...category,
          services: category.services.filter(service =>
            service.viewPoint?.includes(ViewPoint.MEGAMENU),
          ),
        }))
        .filter(category => category.services.length > 0),
    [allRegionCategories],
  );

  /**
   * 저장 버튼 클릭시 동작 함수
   * @description
   * - 저장 결과 토스트는 이 모달에서만 노출합니다. useEditFavoriteService는 GNB 즐겨찾기 순서 변경에도
   *   사용되므로, 훅에서 토스트를 노출하면 순서 변경 시에도 '추가 및 편집' 문구가 노출됩니다.
   * - 실패 시에는 모달을 닫지 않고 재시도할 수 있도록 유지합니다.
   */
  const handleSaveFavoriteServiceClick = () => {
    const flatMapAllServices = allServices.flatMap(category => category.services);
    const selectedServices = selected
      .map(serviceName => flatMapAllServices.find(service => service.name === serviceName))
      .filter(service => service !== undefined);
    editFavoriteService(selectedServices, {
      onSuccess: () => {
        enqueueSuccessToast(t('layout:favorite.modal.add.success_msg'));
        onSubmit?.();
      },
      onError: () => {
        enqueueErrorToast(t('layout:favorite.modal.add.err_msg.default'));
      },
    });
  };

  /** 선택된 즐겨찾기 서비스 갱신 함수 */
  const updateFavoriteServices = (services: string[]) => {
    setSelected(services);
  };

  return (
    <Modal
      open={open}
      onClose={() => !isPending && onClose()}
      loading={isLoading || isPending}
      size="md"
      slotProps={{
        transition: TransitionProps,
        header: {
          title: t('layout:favorite.modal.add.title'),
          description: (
            <>
              <Typography variant="label/regular/sm" display={'inline-block'}>
                {t('layout:favorite.modal.add.description')}
              </Typography>
              <Typography variant="label/regular/sm" display={'inline-block'}>
                {t('layout:favorite.modal.add.helper_text', { max: MAX_FAVORITE_SERVICE_COUNT })}
              </Typography>
            </>
          ),
        },
        body: {
          children: (
            <FavoriteModalBody
              allServices={allServices}
              selected={selected}
              updateServices={updateFavoriteServices}
            />
          ),
        },
        footer: {
          labelMap: {
            submit: t('common:common.action.save'),
            cancel: t('common:common.action.cancel'),
          },
          buttons: [
            <ModalFooter.Button
              key="modal-footer-cancel"
              eventType="cancel"
              disabled={isPending}
              onClick={() => !isPending && onClose()}
            />,
            <ModalFooter.Button
              key="modal-footer-submit"
              eventType="submit"
              disabled={isPending}
              onClick={handleSaveFavoriteServiceClick}
            />,
          ],
        },
      }}
    />
  );
};

export default AddFavoriteModal;
